import clsx from "clsx";
import { useAtom } from "jotai";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { phoneNumberAtom } from "@/utils/atoms";

import { TwoFactorConfirmationInput } from "./TwoFactorConfirmationInput";

// This component is also rendered on the dark 2FA modal background
// (see TwoFactorCodeModal.tsx), so the resend link needs a readable color in both cases.
const resendLinkVariantClasses = {
  light: "text-foreground-primary",
  dark: "text-foreground-primary-inverse",
};

export const AuthenticatorSmsConfirmForm = ({
  resendAction,
}: {
  resendAction: () => void;
}) => {
  const { t } = useTranslation();
  const [phoneNumber] = useAtom(phoneNumberAtom);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-headline-sm font-semibold text-foreground-primary">
          {t("portal:authenticator.sms.verification.title")}
        </h1>
        <p className="text-base text-foreground-primary">
          {t("portal:authenticator.sms.verification.description", {
            number: phoneNumber,
          })}
        </p>
      </div>
      <TwoFactorSMSValidation resendAction={resendAction} />
    </div>
  );
};

export const TwoFactorSMSValidation = ({
  resendAction,
  variant = "light",
}: {
  resendAction: () => void;
  variant?: "light" | "dark";
}) => {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(60);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCountdown = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    setCountdown(60);
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;

          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    startCountdown();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startCountdown]);

  const handleClick = useMemo(
    () =>
      debounce(
        () => {
          startCountdown();
          resendAction();
        },
        1000,
        { leading: true, trailing: false },
      ),
    [startCountdown, resendAction],
  );

  useEffect(() => {
    return () => {
      handleClick.cancel();
    };
  }, [handleClick]);

  return (
    <div className="flex w-fit flex-col gap-2">
      <TwoFactorConfirmationInput variant={variant} />
      <button
        type="button"
        onClick={handleClick}
        disabled={countdown > 0}
        className={clsx(
          "w-fit text-sm font-semibold underline disabled:opacity-50",
          resendLinkVariantClasses[variant],
        )}
      >
        {t("portal:authenticator.sms.verification.resend")}
        {countdown > 0 &&
          ` ${t("portal:authenticator.sms.verification.secondsLeft", { count: countdown })}`}
      </button>
    </div>
  );
};
